import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import Divider from '@zenbusiness/zui/Divider';
import Text from '@zenbusiness/zui/Text';

import useCheckoutComplete from '#client/hooks/useCheckoutComplete';
import { useNextUp } from '#client/hooks/useNextUp';
import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';

import { HomeSection } from '../../HomeSection';
import { INFO_TOOLTIP_MESSAGES } from '../../OrderTracker/constants';
import * as Styled from '../../OrderTracker/OrderTracker.styles';
import OrderTrackerItem from '../../OrderTracker/OrderTrackerItem';
import {
  getTimelineItems,
  getTimelineItemsWithRedirectLinks,
  getTitleMessageSuffix
} from '../../OrderTracker/utils';

const ENTERPRISE_ORDER_TASKS = [
  'llc_formation',
  'ein_creation',
  'registered_agent_service',
  'wfg_initial_report'
];

function EnterpriseOrderTracker() {
  const { orderTasksWithStatus } = useOrderTrackerStatus();
  const { getCheckoutCompleteUrl } = useCheckoutComplete();
  // useNextUp is required to resolve the LLC formation redirect link
  // getCheckoutCompleteUrl does not return a URL for llc_formation
  const { data: nextUpCards = [] } = useNextUp();

  const openExternalLinkEvent = useButtonPressedCallback({
    elementName: 'open_enterprise_order_tracker_external_link',
    pageSlug: 'enterprise_order_tracker'
  });

  const allTimelineItems = getTimelineItems(
    orderTasksWithStatus,
    { enableRaIr: true, includeComplete: true }
  );

  const enterpriseTimelineItems = allTimelineItems.filter(
    (item) => ENTERPRISE_ORDER_TASKS.includes(item.type)
  );

  const timelineItems = getTimelineItemsWithRedirectLinks(
    enterpriseTimelineItems,
    getCheckoutCompleteUrl,
    nextUpCards
  );

  const getItemClickProps = (item) => {
    if (item.linkTo) {
      return {
        isClickable: true,
        eventCallback: openExternalLinkEvent,
        onClick: () => { window.location.href = item.linkTo; }
      };
    }
    return { isClickable: false };
  };

  if (!timelineItems.length) return null;

  const lastItemIndex = timelineItems.length - 1;

  return (
    <HomeSection
      title={(
        <Text color="primary" size="xl" weight="medium">
          <FormattedMessage id="dashboard.orderTracker.title" />
        </Text>
      )}
    >
      <Styled.TrackerCard>
        {timelineItems.map((item, index) => (
          <Styled.ListItemContainer key={item.type}>
            <OrderTrackerItem
              color={item.color}
              ctaMessageId={`dashboard.orderTracker.orderTaskStatus.${item.status}`}
              titleMessageId={`dashboard.orderTracker.orderTaskType.${item.type}`}
              titleMessageSuffix={getTitleMessageSuffix(item)}
              type={item.type}
              showChevron={false}
              hoverTooltipMessageId={item.hoverTooltipMessageId}
              infoTooltipTitleId={INFO_TOOLTIP_MESSAGES[item.type]?.titleId}
              infoTooltipMessageId={INFO_TOOLTIP_MESSAGES[item.type]?.descriptionId}
              {...getItemClickProps(item)}
            />
            {index !== lastItemIndex && (
              <Divider />
            )}
          </Styled.ListItemContainer>
        ))}
      </Styled.TrackerCard>
    </HomeSection>
  );
}

export default EnterpriseOrderTracker;
